import { ASC, DESC } from "const"
import {
    QidDatapoint,
    QidDatapointCreate,
    QidDatapointSave,
    QidDatapointLoad,
    QidProduct,
    QidModel,
    QidModelSafetyFunction,
    QidModelOption,
} from "server/models"
import { GET, POST, DELETE, compare, sort } from "client/store/utils"
import {
    QidDatapointList,
    QidDatapointGroup,
    QidDatapointChanges,
    UpdateQidDependingDevices,
} from "client/store"

const PutNestedIntoParent = (datapoints: readonly QidDatapoint[]) => {
    const datapointList: QidDatapointList = {}
    datapoints.forEach((datapoint) => {
        if (datapoint.datapointId === null) { // parent datapoint
            datapointList[datapoint.id] = {
                ...datapoint,
                datapoints: [],
            }
        } else {
            const parent = datapointList[datapoint.datapointId]
            datapointList[datapoint.datapointId] = {
                ...parent,
                datapoints: parent.datapoints.concat(datapoint),
            }
        }
    })

    // sort nested datapoints by failure mode
    Object.values(datapointList).forEach((datapoint) => {
        datapointList[datapoint.id] = {
            ...datapoint,
            datapoints: datapoint.datapoints.slice().sort((a, b) => (
                // TODO: sort by "failureMode.order" - not exists now
                compare(a.failureModeId, b.failureModeId, ASC)
            )),
        }
    })
    return datapointList
}

const GroupDatapoints = (datapointList: QidDatapointList) => {
    const datapointGroupList: { [key: string]: QidDatapointGroup } = {}
    Object.values(datapointList).forEach((datapoint) => {
        const key = `model_${datapoint.modelId || "null"}`
            + `_safetyFunction_${datapoint.safetyFunctionId || "null"}`
            + `_option_${datapoint.optionId || "null"}`

        const datapointGroupTarget = datapointGroupList[key]

        if (datapointGroupTarget) {
            if (datapoint.datapoints.length > 0) datapointGroupTarget.isNested = true
            datapointGroupTarget.datapoints.push(datapoint)
        } else {
            const newDatapointGroup: QidDatapointGroup = {
                parents: {
                    model: datapoint.model,
                    safetyFunction: datapoint.safetyFunction,
                    option: datapoint.option,
                },
                isNested: datapoint.datapoints.length > 0,
                datapoints: [datapoint],
                tableView: {
                    ids: [],
                    list: {},
                },
            }
            datapointGroupList[key] = newDatapointGroup
        }
    })
    return datapointGroupList
}

export const PrepareDatapoints = (datapoints: readonly QidDatapoint[]) => {
    // put nested datapoints based on failure modes into parent
    const datapointList = PutNestedIntoParent(datapoints)

    // group datapoints by model / safety function / option
    const datapointGroupList = GroupDatapoints(datapointList)

    // order datapoints in each group by createdAt and prepare data for table view
    Object.keys(datapointGroupList).forEach((key) => {
        const datapointGroup = datapointGroupList[key]
        const ids: number[] = []
        const list: QidDatapointList = {}

        datapointGroup.datapoints.sort((a, b) => sort(a, b, "createdAt", DESC))
        datapointGroup.datapoints.forEach((datapoint) => {
            ids.push(datapoint.id)
            list[datapoint.id] = datapoint

            datapoint.datapoints.forEach((nested) => {
                ids.push(nested.id)
                list[nested.id] = nested
            })
        })

        datapointGroup.tableView = { ids, list }
    })

    return Object.values(datapointGroupList)
}

export type ResultDatapointListType = ReturnType<typeof PrepareResultDatapoints>
export const PrepareResultDatapoints = (datapoints: readonly QidDatapoint[]) => {
    // put nested datapoints based on failure modes into parent
    const datapointList = PutNestedIntoParent(datapoints)

    // group datapoints by model / safety function / option
    const datapointGroupList = GroupDatapoints(datapointList)

    return datapointGroupList
}

export const LoadQidDatapointById = (id: number) => (
    GET(`/qidDatapoint/id/${id}`)
    .then((datapoint: QidDatapoint) => datapoint)
)

export const LoadQidDatapoints = (props: QidDatapointLoad) => (
    POST(
        "/qidDatapoint/load",
        JSON.stringify(props),
    ).then((datapoints: QidDatapoint[]) => datapoints)
)

export const LoadQidDatapointsByFailureMode = (failureModeId: number) => (
    GET(`/qidDatapoint/failureMode/${failureModeId}`)
    .then((datapoints: QidDatapoint[]) => datapoints)
)

export const SaveQidDatapoint = (
    id: number,
    datapoint: QidDatapointCreate,
) => {
    const data: QidDatapointSave = {
        id,
        datapoint,
    }
    return POST(
        "/qidDatapoint/save",
        JSON.stringify(data),
    ).then((res) => {
        const newDatapoint = res as QidDatapoint
        return newDatapoint.id || null
    })
}

export const SaveQidDatapoints = (
    newDatapoints: readonly QidDatapoint[],
    existingDatapoints: readonly QidDatapoint[],
    changes: QidDatapointChanges,
    deleteExisting: readonly number[],
    product: QidProduct,
    model: QidModel,
    safetyFunction: QidModelSafetyFunction,
    option: QidModelOption,
) => {
    const promiseArray: Promise<any>[] = []
    const updateDependingDevices: Promise<any>[] = []

    newDatapoints.forEach((row) => {
        const changedDatapoint = changes[row.id] || {}
        const datapoint = {
            ...row,
            ...changedDatapoint,
        }

        const parentDatapoint: QidDatapointCreate = {
            datapointId: null,
            productId: product.id,
            modelId: model ? model.id : null,
            safetyFunctionId: safetyFunction ? safetyFunction.id : null,
            optionId: option ? option.id : null,
            failureModeId: null,
            versionId: datapoint.reference ? datapoint.reference.id : null,
            publicationDate: datapoint.publicationDate,
            sd: datapoint.sd,
            su: datapoint.su,
            dd: datapoint.dd,
            du: datapoint.du,
            ne: datapoint.ne,
            np: datapoint.np,
            createdAt: datapoint.createdAt,
        }

        promiseArray.push(
            SaveQidDatapoint(null, parentDatapoint).then((parentDatapointId) => {
                const nestedPromiseArray: Promise<any>[] = []
                datapoint.datapoints.forEach((nestedRow) => {
                    // failure mode is assigned/removed according to selected safety function
                    if (!nestedRow.failureMode) return

                    const changedNestedDatapoint = changes[nestedRow.id] || {}
                    const nestedDatapoint = {
                        ...nestedRow,
                        ...changedNestedDatapoint,
                    }

                    const childDatapoint: QidDatapointCreate = {
                        datapointId: parentDatapointId,
                        productId: product.id,
                        modelId: model ? model.id : null,
                        safetyFunctionId: safetyFunction ? safetyFunction.id : null,
                        optionId: option ? option.id : null,
                        failureModeId: nestedDatapoint.failureMode.id,
                        versionId: null,
                        publicationDate: null,
                        sd: nestedDatapoint.sd,
                        su: nestedDatapoint.su,
                        dd: nestedDatapoint.dd,
                        du: nestedDatapoint.du,
                        ne: nestedDatapoint.ne,
                        np: nestedDatapoint.np,
                    }

                    nestedPromiseArray.push(
                        SaveQidDatapoint(null, childDatapoint),
                    )
                })
                return Promise.all(nestedPromiseArray)
            }),
        )
    })

    existingDatapoints.forEach((row) => {
        if (deleteExisting.includes(row.id)) {
            promiseArray.push(
                DeleteQidDatapoint(row),
            )
        } else {
            const changedDatapoint = changes[row.id] || {}
            const datapoint = {
                ...row,
                ...changedDatapoint,
            }

            const parentDatapoint: QidDatapointCreate = {
                datapointId: null,
                productId: product.id,
                modelId: model ? model.id : null,
                safetyFunctionId: safetyFunction ? safetyFunction.id : null,
                optionId: option ? option.id : null,
                failureModeId: null,
                versionId: datapoint.reference ? datapoint.reference.id : null,
                publicationDate: datapoint.publicationDate,
                sd: datapoint.sd,
                su: datapoint.su,
                dd: datapoint.dd,
                du: datapoint.du,
                ne: datapoint.ne,
                np: datapoint.np,
            }

            promiseArray.push(
                SaveQidDatapoint(datapoint.id, parentDatapoint).then(() => {
                    const nestedPromiseArray: Promise<any>[] = []
                    datapoint.datapoints.forEach((nestedRow) => {
                        const changedNestedDatapoint = changes[nestedRow.id] || {}
                        const nestedDatapoint = {
                            ...nestedRow,
                            ...changedNestedDatapoint,
                        }

                        const childDatapoint: QidDatapointCreate = {
                            datapointId: datapoint.id,
                            productId: product.id,
                            modelId: model ? model.id : null,
                            safetyFunctionId: safetyFunction ? safetyFunction.id : null,
                            optionId: option ? option.id : null,
                            failureModeId: nestedDatapoint.failureMode.id,
                            versionId: null,
                            publicationDate: null,
                            sd: nestedDatapoint.sd,
                            su: nestedDatapoint.su,
                            dd: nestedDatapoint.dd,
                            du: nestedDatapoint.du,
                            ne: nestedDatapoint.ne,
                            np: nestedDatapoint.np,
                        }

                        nestedPromiseArray.push(
                            SaveQidDatapoint(nestedDatapoint.id, childDatapoint),
                        )
                    })
                    return Promise.all(nestedPromiseArray)
                }),
            )

            updateDependingDevices.push(
                UpdateQidDependingDevices({
                    update: true,
                    qidProductId: product.id,
                    qidModelId: model ? model.id : null,
                    qidSafetyFunctionId: safetyFunction ? safetyFunction.id : null,
                    qidOptionId: option ? option.id : null,
                    qidDatapointId: datapoint.id,
                }),
            )
        }
    })

    return Promise.all(promiseArray).then(() => {
        updateDependingDevices.push(
            UpdateQidDependingDevices({
                update: true,
                qidProductId: product.id,
                qidModelId: model ? model.id : null,
                qidSafetyFunctionId: safetyFunction ? safetyFunction.id : null,
                qidOptionId: option ? option.id : null,
                qidDatapointId: null,
            }),
            UpdateQidDependingDevices({
                update: true,
                qidProductId: product.id,
                qidModelId: model ? model.id : null,
                qidSafetyFunctionId: safetyFunction ? safetyFunction.id : null,
                qidOptionId: null,
            }),
            UpdateQidDependingDevices({
                update: true,
                qidProductId: product.id,
                qidModelId: model ? model.id : null,
                qidSafetyFunctionId: null,
            }),
            UpdateQidDependingDevices({
                update: true,
                qidProductId: product.id,
                qidModelId: null,
            }),
        )
        return Promise.all(updateDependingDevices)
    })
}

export const DeleteQidDatapoint = (datapoint: QidDatapoint) => (
    DELETE(`/qidDatapoint/delete/${datapoint.id}`)
    .then((removedRows: number) => {
        if (removedRows === 1) {
            // update SIL project devices
            return Promise.all([
                UpdateQidDependingDevices({
                    update: false,
                    qidProductId: datapoint.productId,
                    qidModelId: datapoint.modelId,
                    qidSafetyFunctionId: datapoint.safetyFunctionId,
                    qidOptionId: datapoint.optionId,
                    qidDatapointId: datapoint.id,
                }),
                UpdateQidDependingDevices({
                    update: true,
                    qidProductId: datapoint.productId,
                    qidModelId: datapoint.modelId,
                    qidSafetyFunctionId: datapoint.safetyFunctionId,
                    qidOptionId: datapoint.optionId,
                    qidDatapointId: null,
                }),
                UpdateQidDependingDevices({
                    update: true,
                    qidProductId: datapoint.productId,
                    qidModelId: datapoint.modelId,
                    qidSafetyFunctionId: datapoint.safetyFunctionId,
                    qidOptionId: null,
                }),
                UpdateQidDependingDevices({
                    update: true,
                    qidProductId: datapoint.productId,
                    qidModelId: datapoint.modelId,
                    qidSafetyFunctionId: null,
                }),
                UpdateQidDependingDevices({
                    update: true,
                    qidProductId: datapoint.productId,
                    qidModelId: null,
                }),
            ]).then(() => removedRows)
        }
        return removedRows
    })
)
